/**
 * Unified Findings Panel — the universal linked-data surface (#3).
 *
 * Right-side panel showing everything this workspace can cite, as a uniform
 * three-level tree: SOURCE → SECTION → REFERENCE.
 *
 *   Regulatory reference  →  document            →  article/provision + excerpt
 *   Other workspace       →  upstream workspace   →  its OUTPUT (deliverable)
 *   Online search         →  query/topic          →  result
 *   Uploaded document     →  file                 →  page/sheet/chunk
 *
 * Guiding principle: UX = AX. The human's "expand to read" mirrors the agent's
 * `expand_workspace`; each reference carries a one-line summary (seen by
 * default), and the full read is the drill-in. "Other workspace" is fed by the
 * report_library plan-graph join (`linked_outputs`) — we reference a workspace's
 * OUTPUT, never its inner findings/nodes. Concepts are NOT a source (removed).
 */

import React, { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import {
  ArrowLeft, AlertTriangle, Globe2, Paperclip,
  ChevronRight, ChevronDown, Scale, Boxes, Maximize2, ExternalLink, RefreshCw,
  Loader2,
} from 'lucide-react';
import api, { deleteWorkspaceUpload, approveSpec } from '../../apiService';
import type { ConsumedSpec } from '../../apiService';
import { SpecResolutionError } from '../../apiService';
import { useProjectStore } from '../../stores/projectStore';
import { cleanSourceText } from '../../utils/contentFormatter';
import { useFindingSourcePrefetch } from '../../hooks/useFindingSourcePrefetch';
import FindingDetailView from './FindingDetailView';
import OnlineSupportDetailView from './OnlineSupportDetailView';
import OnlineSupportFocusModal from './OnlineSupportFocusModal';
import { SpecGroup, RequirementDetail, reqKey } from '../production/SpecPanel';
import type { DictMarker, DictUploadedFile, LinkedOutput, LinkedOutputReference, LinkedRequirementFindingRef } from '../../types/workspaceState';
import { detectFindingSourceType } from '../../utils/findingSourceType';
import {
  FindingsEmptyState, FindingsListHeader, LINKED_KIND_LABELS, SourceGroup, findingLeafSummary, findingLeafTitle,
  regulatorySource,
  type Finding, type LeafKind, type RefLeaf, type TreeSection, type TreeSource,
} from './findingsTree';
import type { OnlineSupport as OnlineSupportItem } from '../../types/online_support';
import ConfirmDialog from '../ui/ConfirmDialog';
import { useToast } from '../ui/Toast';
import { formatCount, plural } from '../../utils/plural';

// The toggle lives with the list's rows (findingsTree); its importers keep this path.
export { FindingsToggleButton } from './findingsTree';

// =============================================================================
// TYPES
// =============================================================================

/** Citation context from chat reference clicks */
export interface CitationContext {
  context: string;
  citation_text: string;
  label: string;
  node_ids: string[];
}

/** Node data for determining mapped/unmapped status */
interface NodeRef {
  node_id: string;
  label: string;
  finding_ids: string[];
}

interface UnifiedFindingsPanelProps {
  allFindings: Record<string, Finding>;
  allMarkers?: Record<string, DictMarker>;
  nodes?: Record<string, NodeRef>;
  selectedFindingId: string | null;
  citationContext?: CitationContext | null;
  isOpen: boolean;
  workspaceId?: string;
  compartmentId?: string;
  onToggle: () => void;
  onFindingSelect: (findingId: string) => void;
  onClose: () => void;
  onNavigateToNode?: (nodeId: string) => void;
  onDetailOpenChange?: (isDetailOpen: boolean) => void;
  onPanelResize?: (delta: number) => void;
  /** Online-search results pinned to the workspace (BAR-39). */
  allOnlineSupport?: Record<string, OnlineSupportItem>;
  allUploadedFiles?: Record<string, DictUploadedFile>;
  /** OUTPUTS of upstream workspaces feeding into this one — the "Other
   *  workspace" source (#3, resolved read-time from the plan graph). */
  linkedOutputs?: LinkedOutput[];
  /** When set externally (e.g. a report citation chip), open inline detail. */
  selectedOnlineSupportId?: string | null;
  /** When set externally (a documentation `{req:REQ-XXX}` chip), open the
   *  referenced upstream requirement's detail (statement + held findings). */
  selectedRequirementId?: string | null;
  /** Production spec-mode (build/data-prep workspaces): when set together with
   *  `workspaceId` + `authToken`, the panel also fetches the consumed Model
   *  Specs and renders them under "Other workspace" WITH build-tracking
   *  (sign-off, done/open/stale, impl node, verdict, approve) — folding the
   *  former standalone SpecPanel into this one universal panel. Omitted in
   *  research/report mounts, where the panel behaves exactly as before. */
  specProjectId?: string;
  authToken?: string;
  specRefreshSignal?: number;
  /** Controlled focus: open on a `${source_workspace_id}::${requirement_id}`
   *  key (e.g. a NodeInfoBar requirement chip). */
  specFocusKey?: string | null;
  specFocusNonce?: number;
  /** Reports {specCount, openCount} after each spec fetch so the parent can
   *  render its toggle badge without duplicating the fetch. */
  onSpecSummaryChange?: (summary: { specCount: number; openCount: number }) => void;
}


// =============================================================================
// LINKED-OUTPUT DETAIL (drill-in = "expand to read" == expand_workspace)
// =============================================================================

/** One backing finding of a requirement — collapsed by default (citation only),
 *  drills into its full source text on click. The requirement HOLDS the finding;
 *  the reader expands only the ones they want (summary→drill-in). */
function RequirementFindingRow({ fr }: { fr: LinkedRequirementFindingRef }) {
  const [open, setOpen] = useState(false);
  const citation =
    fr.citation_excerpt ||
    [fr.source_document_name, fr.source_reference].filter(Boolean).join(' ') ||
    fr.finding_id;
  const body = (fr.source_text || '').trim();
  return (
    <li className="border border-border-default rounded overflow-hidden">
      <button
        type="button"
        onClick={() => body && setOpen((o) => !o)}
        className={`w-full flex items-start gap-1.5 px-2 py-1.5 text-left ${body ? 'hover:bg-surface-sunken cursor-pointer' : 'cursor-default'} transition-colors`}
        title={body ? 'Show the source text this requirement rests on' : citation}
      >
        {body
          ? (open
              ? <ChevronDown size={12} strokeWidth={1.7} className="mt-0.5 flex-shrink-0 text-fg-tertiary" />
              : <ChevronRight size={12} strokeWidth={1.7} className="mt-0.5 flex-shrink-0 text-fg-tertiary" />)
          : <span className="w-3 flex-shrink-0" />}
        <Scale size={12} strokeWidth={1.6} className="mt-0.5 flex-shrink-0 text-accent" />
        <span className="text-[12px] text-fg font-medium break-words">{citation}</span>
      </button>
      {open && body && (
        <p className="px-2 pb-2 pl-7 text-[12px] text-fg-secondary leading-relaxed whitespace-pre-wrap break-words">
          {cleanSourceText(body)}
        </p>
      )}
    </li>
  );
}

function LinkedOutputDetailView({
  workspaceName, reference, onOpenWorkspace,
}: {
  workspaceName: string;
  reference: LinkedOutputReference;
  onOpenWorkspace: () => void;
}) {
  const findingRefs = reference.kind === 'spec_requirement'
    ? (reference.finding_refs || [])
    : [];
  return (
    <div className="flex-1 overflow-y-auto p-4 min-h-0">
      <div className="flex items-center gap-1.5 mb-2">
        <span className="text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded bg-accent-tint text-accent-hover font-semibold">
          {LINKED_KIND_LABELS[reference.kind] || 'Output'}
        </span>
        <span className="text-[11px] text-fg-tertiary truncate">from {workspaceName}</span>
      </div>
      <h4 className="text-sm font-semibold text-fg mb-2 break-words">{reference.title}</h4>
      {/* Full statement / content — untruncated (the drill-in IS the full read). */}
      {(reference.detail || reference.summary)
        ? <p className="text-[13px] text-fg-secondary leading-relaxed whitespace-pre-wrap break-words">{reference.detail || reference.summary}</p>
        : <p className="text-[13px] text-fg-tertiary italic">No content available.</p>}
      {findingRefs.length > 0 && (
        <div className="mt-4">
          <div className="text-[11px] uppercase tracking-wide text-fg-tertiary font-semibold mb-1.5">
            Grounded in — {plural(findingRefs.length, {
              one: 'the finding',
              other: formatCount(findingRefs.length, { one: 'finding', other: 'findings' }),
            })} this requirement holds
          </div>
          <ul className="space-y-1">
            {findingRefs.map((fr, i) => (
              <RequirementFindingRow key={fr.finding_id || i} fr={fr} />
            ))}
          </ul>
        </div>
      )}
      <button
        type="button"
        onClick={onOpenWorkspace}
        className="mt-4 inline-flex items-center gap-1.5 px-2.5 py-1.5 border border-link/40 text-link hover:bg-link/10 rounded text-[12px] font-semibold transition-colors"
        title={`Open ${workspaceName} to read its full output`}
      >
        <ExternalLink size={13} strokeWidth={1.7} />
        Open {workspaceName}
      </button>
    </div>
  );
}

// =============================================================================
// MAIN COMPONENT
// =============================================================================

export default function UnifiedFindingsPanel({
  allFindings,
  allMarkers,
  nodes,
  selectedFindingId,
  citationContext,
  isOpen,
  workspaceId,
  compartmentId,
  onToggle,
  onFindingSelect,
  onClose,
  onNavigateToNode,
  onDetailOpenChange,
  onPanelResize,
  allOnlineSupport,
  allUploadedFiles,
  linkedOutputs,
  selectedOnlineSupportId,
  selectedRequirementId,
  specProjectId,
  authToken,
  specRefreshSignal,
  specFocusKey,
  specFocusNonce,
  onSpecSummaryChange,
}: UnifiedFindingsPanelProps) {
  const { showToast } = useToast();
  const [detailFindingId, setDetailFindingId] = useState<string | null>(null);
  const [detailOnlineSupportId, setDetailOnlineSupportId] = useState<string | null>(null);
  const [detailLinked, setDetailLinked] = useState<
    { wsId: string; wsName: string; reference: LinkedOutputReference } | null
  >(null);
  const [focusOnlineSupportId, setFocusOnlineSupportId] = useState<string | null>(null);
  const [listWidth, setListWidth] = useState(200);
  const [isResizing, setIsResizing] = useState(false);
  const [deletingUploadId, setDeletingUploadId] = useState<string | null>(null);
  const [pendingUploadDelete, setPendingUploadDelete] = useState<{
    file: DictUploadedFile;
    targetWorkspaceId: string;
    label: string;
  } | null>(null);
  const [uploadDeleteError, setUploadDeleteError] = useState<string | null>(null);
  // Production spec-mode state (folded-in SpecPanel). Inert unless specEnabled.
  const specEnabled = !!(specProjectId && workspaceId && authToken);
  const [specs, setSpecs] = useState<ConsumedSpec[] | null>(null);
  // A read that FAILED, kept apart from a read that found no spec. Rendering
  // [] for both is the client-side half of the defect the backend just fixed:
  // "could not read the spec" and "this workspace consumes no spec" looked
  // identical, so a transient upstream failure read as an empty requirement
  // list with no indication anything had gone wrong.
  const [specsError, setSpecsError] = useState<SpecResolutionError | null>(null);
  const [specRetryNonce, setSpecRetryNonce] = useState(0);
  // UX Standard S-06: the retry disables its button until its read settles.
  // A slow upstream can hold that read for the full read budget, and each
  // extra click would start another read of the same spec.
  const [isRetrying, setIsRetrying] = useState(false);
  const retrySpecs = useCallback(() => setSpecRetryNonce((n) => n + 1), []);
  // FAILURE MEMORY. Every requirement write bumps specRefreshSignal, so a
  // 28-requirement sweep re-reads the spec 28 times per mounted panel. Against
  // a failing upstream each of those can sit for the full read budget, which
  // turns one broken upstream into a queue of doomed requests. After a failure
  // the panel holds the error and waits: only an explicit retry, or a cooled
  // down signal, reads again.
  const lastSpecFailureAt = useRef<number>(0);
  const lastAttemptedRetryNonce = useRef<number>(-1);
  const SPEC_FAILURE_COOLDOWN_MS = 15_000;
  const [selectedReqKey, setSelectedReqKey] = useState<string | null>(null);
  const [chipFindingId, setChipFindingId] = useState<string | null>(null);
  const [approvingWsId, setApprovingWsId] = useState<string | null>(null);
  const projectFindings = useProjectStore(s => s.activeProject?.findings);
  // Source-level expansion (collapsible tree). Sections default expanded;
  // collapsing one adds it to this set.
  const [expandedSources, setExpandedSources] = useState<Set<string>>(
    () => new Set(['regulatory', 'workspace', 'online', 'uploaded']),
  );
  const [collapsedSections, setCollapsedSections] = useState<Set<string>>(() => new Set());

  const currentUserId = useProjectStore(s => s._currentUserId);
  const loadSpecificWorkspace = useProjectStore(s => s.loadSpecificWorkspace);
  const listScrollRef = useRef<HTMLDivElement | null>(null);

  const scrollRowIntoView = useCallback(
    (attr: 'data-finding-id' | 'data-online-id', id: string) => {
      const safeId = typeof CSS !== 'undefined' && CSS.escape ? CSS.escape(id) : id;
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          listScrollRef.current
            ?.querySelector(`[${attr}="${safeId}"]`)
            ?.scrollIntoView({ block: 'center', inline: 'nearest', behavior: 'auto' });
        });
      });
    },
    [],
  );

  const findingsArray = useMemo(() => Object.values(allFindings || {}), [allFindings]);

  // Warm the browser cache for these findings' embedded source documents in
  // the background, so opening a finding renders its EUR-Lex/PDF iframe
  // instantly instead of a fresh ~3-5s fetch.
  useFindingSourcePrefetch(allFindings);

  const onlineSupportItems = useMemo(() => {
    if (!allOnlineSupport) return [] as OnlineSupportItem[];
    return Object.values(allOnlineSupport).sort((a, b) => {
      const aTime = new Date(a.pinned_at || a.retrieved_at || '').getTime() || 0;
      const bTime = new Date(b.pinned_at || b.retrieved_at || '').getTime() || 0;
      return bTime - aTime;
    });
  }, [allOnlineSupport]);

  const uploadedFileItems = useMemo(() => {
    if (!allUploadedFiles) return [] as DictUploadedFile[];
    return Object.values(allUploadedFiles)
      .filter((file) => file.upload_status !== 'deleted')
      .sort((a, b) => {
        const aTime = new Date(a.created_at || '').getTime() || 0;
        const bTime = new Date(b.created_at || '').getTime() || 0;
        return bTime - aTime;
      });
  }, [allUploadedFiles]);

  // -------------------------------------------------------------------------
  // Production spec-mode: fetch the consumed Model Specs (the join behind the
  // former SpecPanel) and render them under "Other workspace". Inert unless
  // specEnabled — research/report mounts pass no spec props.
  // -------------------------------------------------------------------------
  useEffect(() => {
    if (!specEnabled) return;
    // An explicit retry always reads. An automatic refresh does not, while a
    // recent failure is still warm.
    const explicitRetry = specRetryNonce > 0
      && specRetryNonce !== lastAttemptedRetryNonce.current;
    if (
      !explicitRetry
      && lastSpecFailureAt.current
      && Date.now() - lastSpecFailureAt.current < SPEC_FAILURE_COOLDOWN_MS
    ) {
      return;
    }
    lastAttemptedRetryNonce.current = specRetryNonce;
    let cancelled = false;
    if (explicitRetry) setIsRetrying(true);
    api.getWorkspaceRequirements(specProjectId!, workspaceId!, authToken!)
      .then((r) => {
        if (cancelled) return;
        lastSpecFailureAt.current = 0;
        setSpecsError(null);
        setSpecs(r.specs ?? []);
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        lastSpecFailureAt.current = Date.now();
        // Never fall back to an empty list: an unknown spec is not an absent
        // one. Hold the error and render it, with a way to try again.
        setSpecsError(
          error instanceof SpecResolutionError
            ? error
            : new SpecResolutionError({
                code: 'requirement_spec_resolution_failed',
                status: 0,
                reason: error instanceof Error ? error.message : String(error),
                retriable: true,
                message: 'The requirements for this workspace could not be loaded.',
              }),
        );
        setSpecs(null);
      })
      .finally(() => {
        if (!cancelled) setIsRetrying(false);
      });
    return () => {
      cancelled = true;
      // A retry cancelled by a refresh signal never settles here, and the
      // next run may be held by the cooldown, so clear the flag now or the
      // button stays disabled.
      setIsRetrying(false);
    };
  }, [
    specEnabled, specProjectId, workspaceId, authToken, specRefreshSignal,
    specRetryNonce,
  ]);

  useEffect(() => {
    if (!onSpecSummaryChange || specs === null || specsError) return;
    const openCount = specs.reduce(
      (acc, s) => acc + Math.max(0, s.progress.total - s.progress.done), 0,
    );
    onSpecSummaryChange({ specCount: specs.length, openCount });
  }, [specs, specsError, onSpecSummaryChange]);

  // Grounding chips resolve against the build's findings + project-level map
  // (a ref whose Finding lives in another workspace degrades to an inert chip).
  const specAllFindings = useMemo(
    () => ({ ...(projectFindings || {}), ...(allFindings || {}) }) as Record<string, any>,
    [projectFindings, allFindings],
  );

  const handleApproveSpec = useCallback(async (sourceWorkspaceId: string) => {
    setApprovingWsId(sourceWorkspaceId);
    const previous = specs;
    const optimisticAt = new Date().toISOString();
    setSpecs((curr) => (curr || []).map((s) => (
      s.source_workspace_id === sourceWorkspaceId
        ? { ...s, approval: { approved_by: 'you', approved_at: optimisticAt }, approval_current: true }
        : s
    )));
    const result = await approveSpec(sourceWorkspaceId, authToken);
    if (result.success) {
      setSpecs((curr) => (curr || []).map((s) => (
        s.source_workspace_id === sourceWorkspaceId
          ? {
              ...s,
              approval: {
                approved_version_hash: result.approved_version_hash,
                approved_by: result.approved_by || 'you',
                approved_at: result.approved_at || optimisticAt,
              },
              approval_current: true,
            }
          : s
      )));
    } else {
      setSpecs(previous);
    }
    setApprovingWsId(null);
  }, [specs, authToken]);

  // -------------------------------------------------------------------------
  // Build the Source → Section → Reference tree.
  // -------------------------------------------------------------------------
  const sources = useMemo<TreeSource[]>(() => {
    const out: TreeSource[] = [];

    // 1. Regulatory reference — pdf / eurlex / qa / unknown findings, by document.
    const regulatoryFindings: Finding[] = [];
    const workspaceFindings: Finding[] = [];
    for (const f of findingsArray) {
      const st = detectFindingSourceType(f);
      if (st === 'concept') continue;          // Concepts are NOT a source (removed).
      if (st === 'workspace') { workspaceFindings.push(f); continue; }
      if (st === 'user_upload') continue;       // surfaced under Uploaded document.
      regulatoryFindings.push(f);
    }
    const regulatory = regulatorySource(regulatoryFindings);
    if (regulatory) out.push(regulatory);

    // 2. Other workspace — upstream OUTPUTS (plan-graph join) + legacy imported facts.
    // In spec-mode the research upstreams are rendered as rich SpecGroups
    // (extraContent) below, so drop them from the read-only linked_outputs here
    // to avoid double-listing the same workspace.
    const wsSections: TreeSection[] = [];
    const linkedForOther = specEnabled
      ? (linkedOutputs || []).filter((lo) => (lo.workspace_type || '').toLowerCase() !== 'research')
      : (linkedOutputs || []);
    for (const lo of linkedForOther) {
      wsSections.push({
        id: `lo:${lo.workspace_id}`,
        title: lo.workspace_name,
        subtitle: lo.output_spec || lo.deliverable || undefined,
        pendingNote: lo.pending ? 'No output committed yet' : undefined,
        leaves: (lo.references || []).map((ref) => ({
          id: `linked:${lo.workspace_id}:${ref.ref_id}`,
          kind: 'linked' as LeafKind,
          title: ref.title,
          summary: ref.summary,
          badge: LINKED_KIND_LABELS[ref.kind] || null,
          linkedWsId: lo.workspace_id,
          linkedWsName: lo.workspace_name,
          linkedRef: ref,
        })),
      });
    }
    if (workspaceFindings.length > 0) {
      // Legacy manually-imported input facts, grouped by their origin name.
      const byName = new Map<string, Finding[]>();
      for (const f of workspaceFindings) {
        const name = f.source_document_name || 'Imported facts';
        const arr = byName.get(name) || [];
        arr.push(f);
        byName.set(name, arr);
      }
      for (const [name, findings] of Array.from(byName.entries())) {
        wsSections.push({
          id: `wsfind:${name}`,
          title: name,
          leaves: findings.map((f) => ({
            id: `finding:${f.finding_id}`,
            kind: 'finding' as LeafKind,
            title: findingLeafTitle(f),
            summary: findingLeafSummary(f),
            finding: f,
          })),
        });
      }
    }
    // Consumed specs (research, spec-mode) render as SpecGroups via extraContent
    // on this source; count them here so the source header total is honest even
    // when there are no non-research linked_outputs.
    // A failed read counts ZERO consumed specs — counting it as one inflated
    // the source badge with a spec that does not exist. The source is still
    // pushed below so the error state has somewhere to render.
    const specCount = specEnabled ? (specs?.length ?? 0) : 0;
    const specUnreadable = specEnabled && Boolean(specsError);
    if (wsSections.length > 0 || specCount > 0 || specUnreadable) {
      out.push({
        id: 'workspace',
        label: 'Other workspace',
        icon: <Boxes size={12} strokeWidth={1.6} />,
        sections: wsSections,
        count: wsSections.length + specCount,
      });
    }

    // 3. Online search — one section per pinned support; results are references.
    if (onlineSupportItems.length > 0) {
      const sections: TreeSection[] = onlineSupportItems.map((s) => {
        const results = s.references || [];
        const leaves: RefLeaf[] = results.length > 0
          ? results.map((r: any, i: number) => ({
              id: `online:${s.online_support_id}:${i}`,
              kind: 'online' as LeafKind,
              title: r.title || r.host || r.url || `Result ${i + 1}`,
              summary: r.snippet || r.summary || '',
              onlineId: s.online_support_id,
            }))
          : [{
              id: `online:${s.online_support_id}:0`,
              kind: 'online' as LeafKind,
              title: s.summary || s.query,
              summary: s.external_host ? `via ${s.external_host}` : '',
              onlineId: s.online_support_id,
            }];
        return {
          id: `os:${s.online_support_id}`,
          title: s.rationale || s.query,
          subtitle: s.external_host ? `via ${s.external_host}` : undefined,
          leaves,
        };
      });
      out.push({
        id: 'online',
        label: 'Online search',
        icon: <Globe2 size={12} strokeWidth={1.6} />,
        sections,
        count: onlineSupportItems.length,
      });
    }

    // 4. Uploaded document — one section per file.
    if (uploadedFileItems.length > 0) {
      const sections: TreeSection[] = uploadedFileItems.map((file) => ({
        id: `uf:${file.upload_id}`,
        title: file.display_name || file.original_filename || file.upload_id,
        deletableFile: file,
        leaves: [{
          id: `upload:${file.upload_id}`,
          kind: 'upload' as LeafKind,
          title: file.display_name || file.original_filename || file.upload_id,
          summary: `${file.mime_type || 'unknown'} · ${file.upload_status || 'stored'}`,
          file,
        }],
      }));
      out.push({
        id: 'uploaded',
        label: 'Uploaded document',
        icon: <Paperclip size={12} strokeWidth={1.6} />,
        sections,
        count: uploadedFileItems.length,
      });
    }

    return out;
  }, [findingsArray, linkedOutputs, onlineSupportItems, uploadedFileItems, specEnabled, specs, specsError]);

  const totalCount = useMemo(() => sources.reduce((n, s) => n + s.count, 0), [sources]);

  // Index finding/online ids → containing source so external selections can
  // auto-expand the path to a row.
  const findingSourceOf = useMemo(() => {
    const map = new Map<string, string>();
    for (const src of sources) {
      for (const section of src.sections) {
        for (const leaf of section.leaves) {
          if (leaf.kind === 'finding' && leaf.finding) map.set(leaf.finding.finding_id, src.id);
          if (leaf.kind === 'online' && leaf.onlineId) map.set(`online:${leaf.onlineId}`, src.id);
        }
      }
    }
    return map;
  }, [sources]);

  const detailOpen = !!detailFindingId || !!detailOnlineSupportId || !!detailLinked || !!selectedReqKey;

  // Selected requirement (spec-mode) — composite-keyed, resolved against specs.
  const selectedReq = useMemo(() => {
    if (!selectedReqKey || !specs) return null;
    for (const spec of specs) {
      for (const section of spec.sections) {
        for (const req of section.requirements) {
          if (reqKey(spec.source_workspace_id, req.requirement_id) === selectedReqKey) {
            return { spec, section: section.section, req };
          }
        }
      }
    }
    return null;
  }, [selectedReqKey, specs]);
  const chipFinding = chipFindingId ? (specAllFindings[chipFindingId] || null) : null;

  const clearAllDetail = useCallback(() => {
    setDetailFindingId(null);
    setDetailOnlineSupportId(null);
    setDetailLinked(null);
    setSelectedReqKey(null);
    setChipFindingId(null);
  }, []);

  // ---- selection handlers ----
  const handleLeafClick = useCallback((leaf: RefLeaf) => {
    const wasOpen = detailOpen;
    if (leaf.kind === 'finding' && leaf.finding) {
      setDetailFindingId(leaf.finding.finding_id);
      setDetailOnlineSupportId(null);
      setDetailLinked(null);
      setSelectedReqKey(null);
      setChipFindingId(null);
      onFindingSelect(leaf.finding.finding_id);
    } else if (leaf.kind === 'online' && leaf.onlineId) {
      setDetailOnlineSupportId(leaf.onlineId);
      setDetailFindingId(null);
      setDetailLinked(null);
      setSelectedReqKey(null);
      setChipFindingId(null);
    } else if (leaf.kind === 'linked' && leaf.linkedRef && leaf.linkedWsId) {
      setDetailLinked({ wsId: leaf.linkedWsId, wsName: leaf.linkedWsName || 'workspace', reference: leaf.linkedRef });
      setDetailFindingId(null);
      setDetailOnlineSupportId(null);
      setSelectedReqKey(null);
      setChipFindingId(null);
    } else if (leaf.kind === 'upload' && leaf.file) {
      if (leaf.file.graph_node_id && onNavigateToNode) onNavigateToNode(leaf.file.graph_node_id);
      return; // navigates the main view; no detail pane
    }
    if (!wasOpen) window.dispatchEvent(new CustomEvent('finding-detail-open'));
  }, [detailOpen, onFindingSelect, onNavigateToNode]);

  // Requirement selection (spec-mode) — mutually exclusive with the other details.
  const handleReqClick = useCallback((key: string) => {
    const wasOpen = detailFindingId !== null || detailOnlineSupportId !== null || detailLinked !== null || selectedReqKey !== null;
    setSelectedReqKey(key);
    setChipFindingId(null);
    setDetailFindingId(null);
    setDetailOnlineSupportId(null);
    setDetailLinked(null);
    if (!wasOpen) window.dispatchEvent(new CustomEvent('finding-detail-open'));
  }, [detailFindingId, detailOnlineSupportId, detailLinked, selectedReqKey]);

  const handleUploadedFileDelete = useCallback((file: DictUploadedFile) => {
    const targetWorkspaceId = workspaceId || file.workspace_id;
    if (!targetWorkspaceId || !currentUserId) {
      showToast({
        variant: 'error',
        message: 'Unable to delete this upload because workspace access is not ready.',
      });
      return;
    }
    const label = file.display_name || file.original_filename || file.upload_id;
    setUploadDeleteError(null);
    setPendingUploadDelete({ file, targetWorkspaceId, label });
  }, [currentUserId, showToast, workspaceId]);

  const confirmUploadedFileDelete = useCallback(async () => {
    if (!pendingUploadDelete || !currentUserId) return;
    const { file, targetWorkspaceId } = pendingUploadDelete;
    setDeletingUploadId(file.upload_id);
    setUploadDeleteError(null);
    try {
      const result = await deleteWorkspaceUpload(targetWorkspaceId, currentUserId, file.upload_id);
      if (!result.success) {
        setUploadDeleteError(result.error || 'Failed to delete uploaded document.');
        return;
      }
      setPendingUploadDelete(null);
      try {
        // fresh: reads the server's post-delete view, so it must not join a
        // /state request issued before the delete (BAR-248).
        await loadSpecificWorkspace(targetWorkspaceId, currentUserId, { showLoading: false, fresh: true });
      } catch {
        showToast({
          variant: 'warning',
          message: 'Upload deleted, but linked data could not be refreshed. Reload the workspace.',
        });
      }
    } catch (error: any) {
      setUploadDeleteError(error?.message || 'Failed to delete uploaded document.');
    } finally {
      setDeletingUploadId(null);
    }
  }, [currentUserId, loadSpecificWorkspace, pendingUploadDelete, showToast]);

  const toggleSource = useCallback((id: string) => {
    setExpandedSources((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }, []);

  const toggleSection = useCallback((id: string) => {
    setCollapsedSections((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }, []);

  // External finding selection (citation chip) → open detail, expand its source,
  // scroll the row into view.
  useEffect(() => {
    if (!selectedFindingId) return;
    setDetailFindingId(selectedFindingId);
    setDetailOnlineSupportId(null);
    setDetailLinked(null);
    const srcId = findingSourceOf.get(selectedFindingId);
    if (srcId) setExpandedSources((prev) => prev.has(srcId) ? prev : new Set(prev).add(srcId));
    scrollRowIntoView('data-finding-id', selectedFindingId);
  }, [selectedFindingId, findingSourceOf, scrollRowIntoView]);

  useEffect(() => {
    if (!selectedOnlineSupportId) return;
    setDetailOnlineSupportId(selectedOnlineSupportId);
    setDetailFindingId(null);
    setDetailLinked(null);
    const srcId = findingSourceOf.get(`online:${selectedOnlineSupportId}`);
    if (srcId) setExpandedSources((prev) => prev.has(srcId) ? prev : new Set(prev).add(srcId));
    window.dispatchEvent(new CustomEvent('finding-detail-open'));
    scrollRowIntoView('data-online-id', selectedOnlineSupportId);
  }, [selectedOnlineSupportId, findingSourceOf, scrollRowIntoView]);

  // External requirement selection (documentation `{req:REQ-XXX}` chip) → open
  // the referenced upstream requirement's DETAIL (its statement + held findings),
  // not just the panel. Resolve the ref across linked_outputs by ref_id.
  useEffect(() => {
    if (!selectedRequirementId) return;
    const target = `req:${selectedRequirementId}`.toLowerCase();
    for (const lo of (linkedOutputs || [])) {
      const ref = (lo.references || []).find(
        (r) => (r.ref_id || '').toLowerCase() === target,
      );
      if (ref) {
        setDetailFindingId(null);
        setDetailOnlineSupportId(null);
        setSelectedReqKey(null);
        setDetailLinked({
          wsId: lo.workspace_id,
          wsName: lo.workspace_name || 'workspace',
          reference: ref,
        });
        setExpandedSources((prev) => prev.has('workspace') ? prev : new Set(prev).add('workspace'));
        window.dispatchEvent(new CustomEvent('finding-detail-open'));
        break;
      }
    }
  }, [selectedRequirementId, linkedOutputs]);

  // Controlled spec focus (NodeInfoBar requirement chip) → open the requirement,
  // expand the "Other workspace" source. Key may land before specs resolve;
  // selectedReq resolves once they arrive.
  useEffect(() => {
    if (!specFocusKey) return;
    setSelectedReqKey(specFocusKey);
    setChipFindingId(null);
    setDetailFindingId(null);
    setDetailOnlineSupportId(null);
    setDetailLinked(null);
    setExpandedSources((prev) => prev.has('workspace') ? prev : new Set(prev).add('workspace'));
    window.dispatchEvent(new CustomEvent('finding-detail-open'));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [specFocusKey, specFocusNonce]);

  useEffect(() => {
    onDetailOpenChange?.(detailOpen);
  }, [detailOpen, onDetailOpenChange]);

  const handleBackToList = useCallback(() => {
    clearAllDetail();
    window.dispatchEvent(new CustomEvent('finding-detail-close'));
  }, [clearAllDetail]);

  const handleClose = useCallback(() => {
    clearAllDetail();
    setFocusOnlineSupportId(null);
    window.dispatchEvent(new CustomEvent('finding-detail-close'));
    onClose();
  }, [onClose, clearAllDetail]);

  const handlePanelResizeStart = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    setIsResizing(true);
    let lastX = e.clientX;
    const handleMouseMove = (ev: MouseEvent) => {
      const delta = ev.clientX - lastX;
      lastX = ev.clientX;
      onPanelResize?.(delta);
    };
    const handleMouseUp = () => {
      setIsResizing(false);
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  }, [onPanelResize]);

  const detailFinding = detailFindingId
    ? (allFindings[detailFindingId] || findingsArray.find(f => f.finding_id === detailFindingId) || null)
    : null;
  const detailOnlineSupport = detailOnlineSupportId
    ? (allOnlineSupport?.[detailOnlineSupportId]
        || onlineSupportItems.find((item) => item.online_support_id === detailOnlineSupportId)
        || null)
    : null;
  const focusOnlineSupport = focusOnlineSupportId
    ? (allOnlineSupport?.[focusOnlineSupportId]
        || onlineSupportItems.find((item) => item.online_support_id === focusOnlineSupportId)
        || null)
    : null;

  const selectedLeafId = detailFindingId
    ? `finding:${detailFindingId}`
    : detailLinked
      ? `linked:${detailLinked.wsId}:${detailLinked.reference.ref_id}`
      : null;

  // Consumed-spec groups (research, spec-mode) — rendered as extraContent on the
  // "Other workspace" source so the rich build-tracking from the former SpecPanel
  // lives inside the one universal panel.
  const specGroupsNode = (specEnabled && specsError) ? (
    <div
      className="mb-1 rounded border border-warning bg-warning-bg p-3"
      data-testid="spec-resolution-error"
      role="alert"
    >
      <div className="flex items-start gap-2">
        <AlertTriangle
          size={14}
          strokeWidth={1.8}
          className="mt-0.5 flex-shrink-0 text-warning"
        />
        <div className="min-w-0 flex-1">
          <div className="text-xs font-medium text-warning-ink">
            The requirements could not be read
          </div>
          <div className="mt-1 text-[11px] leading-relaxed text-fg-secondary">
            This is not an empty spec — the upstream research workspace could
            not be reached, so what this build has to satisfy is unknown.
          </div>
          {specsError.reason && (
            <div className="mt-1 break-words font-mono text-[10px] text-fg-tertiary">
              {specsError.reason}
            </div>
          )}
          {specsError.workspaces.length > 0 && (
            <div className="mt-1 break-words text-[10px] text-fg-tertiary">
              Could not read: {specsError.workspaces.join(', ')}
            </div>
          )}
          {specsError.retriable ? (
            <button
              type="button"
              onClick={retrySpecs}
              disabled={isRetrying}
              aria-busy={isRetrying}
              data-testid="spec-resolution-retry"
              className="mt-2 inline-flex items-center gap-1 rounded border border-border-default bg-surface px-2 py-1 text-[11px] text-fg hover:bg-primary-tint disabled:cursor-wait disabled:opacity-50"
            >
              {isRetrying ? (
                <Loader2 size={11} strokeWidth={1.8} className="animate-spin" aria-hidden="true" />
              ) : (
                <RefreshCw size={11} strokeWidth={1.8} />
              )}
              Try again
            </button>
          ) : (
            <div className="mt-2 text-[10px] text-fg-tertiary">
              Retrying will not help — this needs an operator.
            </div>
          )}
        </div>
      </div>
    </div>
  ) : (specEnabled && specs && specs.length > 0) ? (
    <div className="space-y-2 mb-1">
      {specs.map((spec) => (
        <SpecGroup
          key={spec.source_workspace_id}
          spec={spec}
          selectedKey={selectedReqKey}
          approving={approvingWsId === spec.source_workspace_id}
          onRequirementClick={handleReqClick}
          onApprove={handleApproveSpec}
        />
      ))}
    </div>
  ) : null;

  if (!isOpen) return null;

  return (
    <div className="h-full bg-surface border-l border-border-default shadow-sm flex overflow-hidden relative">
      {/* Left-edge resize handle */}
      {onPanelResize && (
        <div
          className={`absolute left-0 top-0 bottom-0 w-1 cursor-col-resize z-10 transition-colors ${
            isResizing ? 'bg-accent' : 'bg-transparent hover:bg-accent-tint'
          }`}
          onMouseDown={handlePanelResizeStart}
          title="Drag to resize"
        />
      )}

      {/* Tree (master list) */}
      <div
        className="flex-shrink-0 border-r border-border-default flex flex-col h-full"
        style={{ width: detailOpen ? listWidth : '100%' }}
      >
        <FindingsListHeader title="Linked data" count={totalCount} onClose={handleClose} />
        <div ref={listScrollRef} className="flex-1 overflow-y-auto p-1.5">
          {sources.length === 0 ? (
            <FindingsEmptyState text="No linked data yet" />
          ) : (
            sources.map((source) => (
              <SourceGroup
                key={source.id}
                source={source}
                expanded={expandedSources.has(source.id)}
                onToggle={() => toggleSource(source.id)}
                collapsedSections={collapsedSections}
                onSectionToggle={toggleSection}
                selectedLeafId={selectedLeafId}
                selectedOnlineId={detailOnlineSupportId}
                onLeafClick={handleLeafClick}
                onLeafDelete={handleUploadedFileDelete}
                deletingUploadId={deletingUploadId}
                extraContent={source.id === 'workspace' ? specGroupsNode : undefined}
              />
            ))
          )}
        </div>
      </div>

      {/* List/detail resize handle */}
      {detailOpen && (
        <div
          className="w-1 flex-shrink-0 cursor-col-resize hover:bg-accent-tint transition-colors"
          onMouseDown={(e) => {
            e.preventDefault();
            let lastX = e.clientX;
            const onMove = (ev: MouseEvent) => {
              const delta = ev.clientX - lastX;
              lastX = ev.clientX;
              setListWidth(w => Math.max(140, Math.min(360, w + delta)));
            };
            const onUp = () => {
              document.removeEventListener('mousemove', onMove);
              document.removeEventListener('mouseup', onUp);
            };
            document.addEventListener('mousemove', onMove);
            document.addEventListener('mouseup', onUp);
          }}
        />
      )}

      {/* Finding detail */}
      {detailFindingId && detailFinding && (
        <div className="flex-1 flex flex-col min-w-0 min-h-0">
          <div className="px-4 py-1.5 flex items-center gap-2 border-b border-border-default bg-bg-warm flex-shrink-0">
            <button onClick={handleBackToList} className="p-1 hover:bg-bg-warm rounded transition-colors" title="Back to list only">
              <ArrowLeft size={16} className="text-fg-tertiary" />
            </button>
            <h3 className="text-sm font-semibold text-fg truncate">Reference detail</h3>
          </div>
          <FindingDetailView
            finding={detailFinding}
            citationContext={citationContext}
            allFindings={allFindings}
            allMarkers={allMarkers}
            workspaceId={workspaceId}
            compartmentId={compartmentId}
            onNavigateToNode={onNavigateToNode}
            onFindingClick={(id: string) => handleLeafClick({ id: `finding:${id}`, kind: 'finding', title: '', finding: allFindings[id] })}
          />
        </div>
      )}

      {/* Online-support inline detail */}
      {detailOnlineSupportId && detailOnlineSupport && !detailFindingId && (
        <div className="flex-1 flex flex-col min-w-0 min-h-0">
          <div className="px-4 py-1.5 flex items-center gap-2 border-b border-border-default bg-bg-warm flex-shrink-0">
            <button onClick={handleBackToList} className="p-1 hover:bg-bg-warm rounded transition-colors" title="Back to list only">
              <ArrowLeft size={16} className="text-fg-tertiary" />
            </button>
            <h3 className="text-sm font-semibold text-fg truncate flex-1 min-w-0">Online support detail</h3>
            <button
              type="button"
              onClick={() => setFocusOnlineSupportId(detailOnlineSupportId)}
              className="inline-flex items-center gap-1 px-2 py-1 border border-link/40 text-link hover:bg-link/10 rounded text-[11px] font-semibold transition-colors flex-shrink-0"
              title="Explore online support deeper — open full-screen focus view"
              aria-label="Explore online support deeper — open full-screen focus view"
            >
              <Maximize2 size={11} strokeWidth={1.5} />
              <span className="hidden sm:inline">Explore deeper</span>
            </button>
          </div>
          <OnlineSupportDetailView support={detailOnlineSupport} nodes={undefined} onNavigateToNode={onNavigateToNode} />
        </div>
      )}

      {/* Linked-output (other workspace) detail */}
      {detailLinked && !detailFindingId && !detailOnlineSupportId && (
        <div className="flex-1 flex flex-col min-w-0 min-h-0">
          <div className="px-4 py-1.5 flex items-center gap-2 border-b border-border-default bg-bg-warm flex-shrink-0">
            <button onClick={handleBackToList} className="p-1 hover:bg-bg-warm rounded transition-colors" title="Back to list only">
              <ArrowLeft size={16} className="text-fg-tertiary" />
            </button>
            <h3 className="text-sm font-semibold text-fg truncate">Linked output</h3>
          </div>
          <LinkedOutputDetailView
            workspaceName={detailLinked.wsName}
            reference={detailLinked.reference}
            onOpenWorkspace={() => {
              if (currentUserId) loadSpecificWorkspace(detailLinked.wsId, currentUserId, { showLoading: true });
            }}
          />
        </div>
      )}

      {/* Requirement detail (spec-mode) — the consumed-spec requirement's
          statement, this build's status, validator verdict and grounding chips. */}
      {selectedReqKey && selectedReq && !chipFinding && (
        <div className="flex-1 flex flex-col min-w-0 min-h-0">
          <div className="px-4 py-1.5 flex items-center gap-2 border-b border-border-default bg-bg-warm flex-shrink-0">
            <button onClick={handleBackToList} className="p-1 hover:bg-bg-warm rounded transition-colors" title="Back to list only">
              <ArrowLeft size={16} className="text-fg-tertiary" />
            </button>
            <h3 className="text-sm font-semibold text-fg truncate">Requirement detail</h3>
          </div>
          <RequirementDetail
            spec={selectedReq.spec}
            section={selectedReq.section}
            req={selectedReq.req}
            allFindings={specAllFindings}
            onFindingChipClick={setChipFindingId}
            onNavigateToNode={onNavigateToNode}
          />
        </div>
      )}

      {/* Grounding-finding detail behind a requirement chip — the ref points at
          a real Finding, so FindingDetailView renders it; back returns to the req. */}
      {selectedReqKey && chipFinding && (
        <div className="flex-1 flex flex-col min-w-0 min-h-0">
          <div className="px-4 py-1.5 flex items-center gap-2 border-b border-border-default bg-bg-warm flex-shrink-0">
            <button onClick={() => setChipFindingId(null)} className="p-1 hover:bg-bg-warm rounded transition-colors" title="Back to requirement">
              <ArrowLeft size={16} className="text-fg-tertiary" />
            </button>
            <h3 className="text-sm font-semibold text-fg truncate">Finding detail</h3>
          </div>
          <FindingDetailView
            finding={chipFinding}
            allFindings={specAllFindings}
            allMarkers={allMarkers}
            workspaceId={workspaceId}
            compartmentId={compartmentId}
            onNavigateToNode={onNavigateToNode}
            onFindingClick={(id: string) => setChipFindingId(id)}
          />
        </div>
      )}

      <ConfirmDialog
        open={pendingUploadDelete !== null}
        onCancel={() => {
          if (deletingUploadId === null) {
            setPendingUploadDelete(null);
            setUploadDeleteError(null);
          }
        }}
        onConfirm={confirmUploadedFileDelete}
        title={
          pendingUploadDelete
            ? `Delete uploaded document "${pendingUploadDelete.label}"?`
            : ''
        }
        description="This removes the upload and its linked references from this workspace."
        error={uploadDeleteError}
        confirmLabel="Delete"
        variant="destructive"
        submitting={deletingUploadId !== null}
      />

      {/* Online-support focus modal */}
      {focusOnlineSupport && (
        <OnlineSupportFocusModal
          isOpen={focusOnlineSupportId !== null}
          support={focusOnlineSupport}
          nodes={undefined}
          onClose={() => setFocusOnlineSupportId(null)}
          onNavigateToNode={onNavigateToNode}
        />
      )}
    </div>
  );
}
